"use client"

import { useState, useEffect, useCallback, useRef } from "react"

export type FabCorner = "bottom-right" | "bottom-left" | "top-right" | "top-left"

const STORAGE_KEY = "fab-corner"
const DEFAULT_CORNER: FabCorner = "bottom-right"

function isValidCorner(v: unknown): v is FabCorner {
  return v === "bottom-right" || v === "bottom-left" || v === "top-right" || v === "top-left"
}

function readCorner(): FabCorner {
  if (typeof window === "undefined") return DEFAULT_CORNER
  const saved = localStorage.getItem(STORAGE_KEY)
  return isValidCorner(saved) ? saved : DEFAULT_CORNER
}

function nearestCorner(x: number, y: number): FabCorner {
  const vw = window.innerWidth
  const vh = window.innerHeight
  const isRight = x > vw / 2
  const isBottom = y > vh / 2
  if (isBottom && isRight) return "bottom-right"
  if (isBottom && !isRight) return "bottom-left"
  if (!isBottom && isRight) return "top-right"
  return "top-left"
}

/**
 * Shared corner state for both FABs with drag-to-reposition.
 * Returns the corner, position classes, and drag handler props.
 */
export function useFabCorner() {
  const [corner, setCornerState] = useState<FabCorner>(DEFAULT_CORNER)
  const [dragPos, setDragPos] = useState<{ x: number; y: number } | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const dragStartRef = useRef({ x: 0, y: 0, startX: 0, startY: 0, moved: false })

  // Hydrate from localStorage
  // legitimate external sync: localStorage → state on mount + cross-tab storage events
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    setCornerState(readCorner())
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && isValidCorner(e.newValue)) {
        setCornerState(e.newValue)
      }
    }
    window.addEventListener("storage", onStorage)
    return () => window.removeEventListener("storage", onStorage)
  }, [])

  /* eslint-enable react-hooks/set-state-in-effect */
  const setCorner = useCallback((c: FabCorner) => {
    setCornerState(c)
    localStorage.setItem(STORAGE_KEY, c)
    // Dispatch a custom event so both components on the same page stay in sync
    window.dispatchEvent(new CustomEvent("fab-corner-change", { detail: c }))
  }, [])

  // Listen for same-page sync events
  useEffect(() => {
    const handler = (e: Event) => {
      const c = (e as CustomEvent).detail
      if (isValidCorner(c)) setCornerState(c)
    }
    window.addEventListener("fab-corner-change", handler)
    return () => window.removeEventListener("fab-corner-change", handler)
  }, [])

  // Drag handlers — onPointerDown attaches document-level listeners so
  // tracking works even when the pointer leaves the small FAB button.
  const onPointerDown = useCallback((e: React.PointerEvent) => {
    const el = e.currentTarget as HTMLElement
    const rect = el.getBoundingClientRect()
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      startX: rect.left + rect.width / 2,
      startY: rect.top + rect.height / 2,
      moved: false,
    }

    const onMove = (ev: PointerEvent) => {
      const dx = ev.clientX - dragStartRef.current.x
      const dy = ev.clientY - dragStartRef.current.y
      // Only start dragging after 8px movement to avoid interfering with clicks
      if (!dragStartRef.current.moved && Math.abs(dx) + Math.abs(dy) < 8) return
      dragStartRef.current.moved = true
      setIsDragging(true)
      setDragPos({
        x: dragStartRef.current.startX + dx,
        y: dragStartRef.current.startY + dy,
      })
    }

    const onUp = (ev: PointerEvent) => {
      document.removeEventListener("pointermove", onMove)
      document.removeEventListener("pointerup", onUp)

      if (dragStartRef.current.moved) {
        ev.preventDefault()
        const dx = ev.clientX - dragStartRef.current.x
        const dy = ev.clientY - dragStartRef.current.y
        const cx = dragStartRef.current.startX + dx
        const cy = dragStartRef.current.startY + dy
        setCorner(nearestCorner(cx, cy))
      }
      setIsDragging(false)
      setDragPos(null)
      // Delay reset so the click handler can still check wasDrag()
      requestAnimationFrame(() => { dragStartRef.current.moved = false })
    }

    document.addEventListener("pointermove", onMove)
    document.addEventListener("pointerup", onUp)
  }, [setCorner])

  // Whether the last pointer interaction was a drag (not a click)
  const wasDrag = useCallback(() => dragStartRef.current.moved, [])

  const dragHandlers = { onPointerDown }

  return { corner, setCorner, dragHandlers, dragPos, isDragging, wasDrag }
}

/** CSS position classes for the FAB stack corner.
 *  Physical on purpose: a corner is the operator's physical choice and stays put
 *  in every language (locale audit 2026-09-26). What follows the text direction
 *  is only where the FAB steps aside to and which tables make room for it
 *  (hooks/use-fab-auto-corner.ts).
 *  Primary + secondary both return the SAME corner classes so the two FABs overlap
 *  at rest. The fan-out delta is applied separately via `fanOutOffsetClasses` on
 *  group-hover.
 *
 *  @deprecated The second `_offset` argument is ignored — positions are now
 *  uniform per corner. The parameter is kept for call-site compatibility
 *  during the stacked-FAB rollout; it will be removed once Tasks 3 + 4 land
 *  and the last call site drops it. */
export function fabPositionClasses(corner: FabCorner, _offset: "primary" | "secondary" = "primary"): string {
  const map: Record<FabCorner, string> = {
    "bottom-right": "bottom-6 right-6",
    "bottom-left":  "bottom-6 left-6",
    "top-right":    "top-6 right-6",
    "top-left":     "top-6 left-6",
  }
  return map[corner]
}

/** Group-data-driven transform that translates a stacked FAB into its expanded
 *  slot. The stack's open/closed state lives in JS on the host via
 *  `data-stack-open`; this helper emits Tailwind selectors that fire when any
 *  ancestor `.group/fab-stack` carries that attribute. Kept group-hover +
 *  group-focus-within as CSS-only fallbacks for keyboard + reduced-JS paths. */
export function fanOutOffsetClasses(corner: FabCorner): string {
  const isBottom = corner.startsWith("bottom")
  const dir = isBottom ? "-translate-y-16" : "translate-y-16"
  const selectors = [
    `group-hover/fab-stack:${dir}`,
    `group-focus-within/fab-stack:${dir}`,
    `group-data-[stack-open=true]/fab-stack:${dir}`,
  ].join(" ")
  return `transition-transform duration-[380ms] ease-[cubic-bezier(.2,.9,.3,1.4)] motion-reduce:transition-none ${selectors}`
}
